import React, { useMemo } from 'react';

import { Grid } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import styled from '@material-ui/core/styles/styled';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { useTranslation } from 'react-i18next';
import Swiper from 'react-id-swiper';

import Pathway from '../../../../components/Pathway/Pathway';
import { BIG_MARGIN, colorsPalette, LARGE_MARGIN, MIDDLE_MARGIN } from '../../../../theme';
import { Course, Pathway as PathwayType } from '../../../../types';
import { secondsToHHHmmm } from '../../../../utils/helpers';
import CourseCard from '../../../CourcesListPage/components/CourseCard/CourseCard';
import CourseCardWide from '../CourseCardWide/CourseCardWide';

const getSwiperSettings = (isMobile) => ({
  slidesPerView: isMobile ? 1 : 2,
  spaceBetween: 20,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
  mousewheel: {
    releaseOnEdges: true,
    forceToAxis: true,
    invert: true,
  },
  navigation: true,
  direction: 'horizontal',
  rebuildOnUpdate: true,
  slidesOffsetAfter: 0,
});

interface TryCourseProps {
  coursesList: Course[];
  pathways?: PathwayType[];
  circleImageUrl?: string | null;
  width: Breakpoint;
}

const TryCourses = ({ circleImageUrl, width, coursesList, pathways }: TryCourseProps) => {
  const { t } = useTranslation();

  const isTablet = !isWidthUp('md', width);
  const isMobile = !isWidthUp('sm', width);
  const CCard = isTablet ? CourseCard : CourseCardWide;

  const swiperSettings = useMemo(() => getSwiperSettings(isMobile), [isMobile]);

  return (
    <Wrapper>
      <Container>
        <Heading variant="h2">
          {coursesList.length === 0 && t('page.course.tryPathways')}
          {coursesList.length === 1 && t('page.course.tryCourse')}
          {coursesList.length === 2 && t('page.course.tryCourses')}
        </Heading>
        {coursesList.length === 0 && pathways && (
          <GridWrapper>
            {isTablet ? (
              <Swiper {...swiperSettings}>
                {pathways.map(({ id, name, count_videos, duration, description, image_url }) => {
                  const durationLabel = secondsToHHHmmm(duration);

                  return (
                    <PathwayWrapper key={id}>
                      <Pathway
                        videoWidth={6}
                        name={name}
                        imageUrl={image_url}
                        description={description}
                        duration={durationLabel}
                        countVideos={count_videos}
                        withHover={false}
                      />
                    </PathwayWrapper>
                  );
                })}
              </Swiper>
            ) : (
              <StyledGrid container spacing={4}>
                {pathways.map(({ id, name, count_videos, duration, description, image_url }) => {
                  const durationLabel = secondsToHHHmmm(duration);

                  return (
                    <Grid xs={12} sm={6} md={4} key={id} item>
                      <PathwayWrapper key={id}>
                        <Pathway
                          videoWidth={6}
                          name={name}
                          imageUrl={image_url}
                          description={description}
                          duration={durationLabel}
                          countVideos={count_videos}
                          withHover={false}
                        />
                      </PathwayWrapper>
                    </Grid>
                  );
                })}
              </StyledGrid>
            )}
          </GridWrapper>
        )}
        {coursesList.length > 0 && (
          <Grid container spacing={3}>
            {coursesList.map(
              ({ id, slug, svg_icon, image_url, name, description, general_info }) => (
                <Grid key={id} xs={12} item>
                  <CCard
                    link={'courses/' + slug}
                    circleImageUrl={circleImageUrl}
                    logoSvg={svg_icon}
                    image={image_url}
                    title={name}
                    description={description}
                    meta={general_info}
                  />
                </Grid>
              ),
            )}
          </Grid>
        )}
      </Container>
    </Wrapper>
  );
};

export default withWidth()(TryCourses);

const PathwayWrapper = styled('div')(({ theme }) => ({
  paddingTop: 20,
  paddingRight: 20,
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    height: 350,
  },
}));
const StyledGrid = styled(Grid)({
  position: 'relative',
  zIndex: 1,
  height: '100%',
});
const GridWrapper = styled('div')({
  maxWidth: 1060,
  margin: '0 auto',
});
const Heading = styled(Typography)(({ theme }) => ({
  marginBottom: 24,
  textAlign: 'center',
  fontSize: 24,
  [theme.breakpoints.up('md')]: {
    fontSize: 28,
    marginBottom: BIG_MARGIN,
  },
}));
const Wrapper = styled('div')(({ theme }) => ({
  paddingTop: MIDDLE_MARGIN,
  paddingBottom: MIDDLE_MARGIN,
  background: colorsPalette.blackSecondary,
  [theme.breakpoints.up('md')]: {
    paddingTop: LARGE_MARGIN,
    paddingBottom: LARGE_MARGIN,
  },
}));
